import { computed } from 'vue'
import { decodeJWT } from '@/utils/jwtDecoder'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { getRoleFromCookie } from '@/utils/roleHelpers'

// Valid user roles from backend: COMMON, MENTOR, FELLOW, GUIDE
export type UserRole = 'COMMON' | 'MENTOR' | 'FELLOW' | 'GUIDE' | ''

// Roles that have management access (can access /manage routes)
const MANAGEMENT_ROLES: string[] = ['GUIDE', 'MENTOR']

/**
 * Helper function to determine user role from multiple sources
 * Flow: JWT token -> user_role cookie -> empty string
 * Returns normalized uppercase role: 'GUIDE', 'MENTOR', 'FELLOW', 'COMMON', or empty string
 */
export function getUserRole(): UserRole {
  // Priority 1: Try JWT token from localStorage
  const token = localStorage.getItem('token')
  if (token) {
    try {
      const decoded = decodeJWT(token)
      if (decoded?.role) {
        const role = decoded.role.toUpperCase() as UserRole
        console.debug('[useAuth] Role from JWT token:', role)
        return role
      }
      console.debug('[useAuth] JWT token decoded but no role claim. Token keys:', Object.keys(decoded || {}))
    } catch (err) {
      console.warn('[useAuth] Failed to decode token in getUserRole:', err)
    }
  }

  // Priority 2: Try user_role cookie (if token not found or invalid)
  const roleFromCookie = getRoleFromCookie()
  if (roleFromCookie) {
    console.debug('[useAuth] Role from user_role cookie:', roleFromCookie)
    return roleFromCookie as UserRole
  }

  // No role found - return empty string
  console.debug('[useAuth] Could not determine role, returning empty string')
  return ''
}

/**
 * Check if a role has management access (GUIDE or MENTOR)
 */
export function hasManagementAccess(role: string): boolean {
  return MANAGEMENT_ROLES.includes(role.toUpperCase())
}

export function useAuth() {
  const userStore = useUserStore()
  const authStore = useAuthStore()

  /**
   * Get current user role with priority:
   * 1. authStore.userRole (REACTIVE - set from cookie/JWT refresh in cross-subdomain auth)
   * 2. userStore.currentUser.role (REACTIVE - set from API response)
   * 3. user_role cookie (readable, available cross-subdomain)
   * 4. JWT token from localStorage
   * 5. empty string
   * 
   * This ensures reactivity when cross-subdomain auth sets the role in the store
   */
  const getCurrentUserRole = (): UserRole => {
    // Priority 1: Check authStore.userRole FIRST (REACTIVE!)
    // This is set when role is found from cookie/JWT in cross-subdomain auth scenarios
    if (authStore.userRole) {
      const role = authStore.userRole.toUpperCase() as UserRole
      console.debug('[useAuth] Role from auth store:', role)
      return role
    }
    
    // Priority 2: Check userStore.currentUser.role (REACTIVE!)
    // This is critical for cross-subdomain auth where checkAuth() sets userStore.currentUser
    if (userStore.currentUser?.role) {
      const role = userStore.currentUser.role.toUpperCase() as UserRole
      console.debug('[useAuth] Role from userStore.currentUser:', role)
      return role
    }
    
    // Priority 3: Check user_role cookie (readable, available cross-subdomain)
    // Backend sets this with httponly=False, so JavaScript CAN read it
    const cookieRole = getRoleFromCookie()
    if (cookieRole) {
      console.debug('[useAuth] Role from user_role cookie:', cookieRole)
      return cookieRole as UserRole
    }
    
    // Fallback to token for initial render (before checkAuth completes)
    return getUserRole()
  }

  /**
   * Check if current user has management access (GUIDE or MENTOR role)
   */
  const isGuide = computed(() => {
    const role = getCurrentUserRole()
    const hasAccess = MANAGEMENT_ROLES.includes(role)
    console.debug('[useAuth.isGuide] Role:', role || '(empty)', '-> hasAccess:', hasAccess)
    return hasAccess
  })

  /**
   * Check if current user is a MENTOR specifically
   */
  const isMentor = computed(() => {
    const role = getCurrentUserRole()
    return role === 'MENTOR'
  })

  /**
   * Check if current user is a FELLOW
   */
  const isFellow = computed(() => {
    const role = getCurrentUserRole()
    return role === 'FELLOW'
  })

  /**
   * Check if current user is COMMON (limited access)
   */
  const isCommon = computed(() => {
    const role = getCurrentUserRole()
    return role === 'COMMON'
  })

  /**
   * Check if current user is an admin.
   * Reads from the reactive auth store, which is hydrated from localStorage
   * on store creation and updated whenever new JWTs are received.
   */
  const isAdmin = computed(() => authStore.isAdmin)

  // Export the role for components that need it
  const userRole = computed(() => getCurrentUserRole())

  return {
    isGuide,      // true for GUIDE or MENTOR (management access)
    isMentor,     // true only for MENTOR
    isFellow,     // true only for FELLOW
    isCommon,     // true only for COMMON
    isAdmin,      // true if user has is_admin flag
    userRole,     // the actual role string
    hasManagementAccess // helper function
  }
} 